import { Badge, cn, createTestIdHelper } from '@heartplus/ui';
import type { ComponentPropsWithoutRef } from 'react';

export type TabCountBadgeTone = 'attention' | 'neutral';

const testId = createTestIdHelper('tab-count-badge');

// tone owns the palette; structure/shape stays shared. Static hover (it's an
// indicator, not a control). attention = red (needs-attention counts);
// neutral = gray (a plain count carries no severity — color stays reserved for state).
const TONE_CLASS: Record<TabCountBadgeTone, string> = {
  attention:
    'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950',
  neutral:
    'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800',
};

export type TabCountBadgeProps = ComponentPropsWithoutRef<typeof Badge> & {
  count: number;
  tone?: TabCountBadgeTone;
  /**
   * Renders this instead of the bare `count`, for a count that needs qualifying —
   * e.g. `"12 of 47"` when a filter is narrowing the list. `count` is still required
   * so callers can't drift into passing arbitrary text: it stays the semantic value,
   * and `label` only changes how it reads.
   */
  label?: string;
};

/**
 * The count-on-a-tab badge idiom, sharing one shape and palette across the tab
 * strips defined by `tabbed-navigation-styles`. Carries no margin — spacing
 * comes from the tab strip's flex gap. Rest props (`aria-label`, a `data-testid`
 * override, …) forward to the underlying `Badge`.
 */
export function TabCountBadge({
  count,
  tone = 'attention',
  label,
  className,
  ...props
}: Readonly<TabCountBadgeProps>) {
  return (
    <Badge
      data-testid={testId('root')}
      className={cn(
        'rounded-md px-2 py-0.5 text-xs font-medium tabular-nums',
        TONE_CLASS[tone],
        className,
      )}
      {...props}
    >
      {label ?? count}
    </Badge>
  );
}
